import Node from '../node.js';
import { PARAM_TYPE, PARAM_UNITS } from '../connector.js';
import Spline from '../spline.js';
import Extrude from '../../extrude.js';
import InputConnector from '../inputConnector.js';
import { UV_SCALE_MULT } from '../geoUtils.js';

export default class NodeLoft extends Node
{
	constructor(pParent)
	{
		super(pParent);

		this.addInput(PARAM_TYPE.INTEGER, 'num_shapes', 2, { minValue: 2, maxValue: 10 });

		this.addInput(PARAM_TYPE.FLOAT, 'smooth_angle', 0.0, { minValue: 0, maxValue: 180, units: PARAM_UNITS.DEGREES });
		this.addInputsToGroup('Shape Attributes', ['smooth_angle']);

		this.addInput(PARAM_TYPE.FLOAT, 'u_offset', 0.0, { minValue: -1, maxValue: 1, units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.FLOAT, 'v_offset', 0.0, { minValue: -1, maxValue: 1, units: PARAM_UNITS.METERS });
		this.addInput(PARAM_TYPE.FLOAT, 'uv_rotation', 0.0, { minValue: -180, maxValue: 180, units: PARAM_UNITS.DEGREES });
		this.addInputsToGroup('UV Coordinates', ['u_offset', 'v_offset', 'uv_rotation']);

		this.onInputChangedSubclass('num_shapes');

		this.addOutput(PARAM_TYPE.GEOMETRY, 'geo');
	}

	getType()
	{
		return 'loft';
	}

	getRefreshEnableStatusAfterParamChange(strInputName)
	{
		return strInputName == 'num_shapes';
	}

	onInputChangedSubclass(strInputName)
	{
		if(strInputName !== 'num_shapes')
			return;

		const tempShape = {"points":[{"point":{"x":-0.1,"y":0,"z":-0.1},"type":0},{"point":{"x":-0.1,"y":0,"z":0.1},"type":0},{"point":{"x":0.1,"y":0,"z":0.1},"type":0},{"point":{"x":0.1,"y":0,"z":-0.1},"type":0}],"isClosed":true};

		let pInput;
		let pNewInputs = [this.getInput('num_shapes')];
		let iNumShapes = this.getLastInputResult('num_shapes');
		let iDistStep = 1.0/(iNumShapes - 1);
		for(let idx = 0; idx < iNumShapes; idx++)
		{
			let tempSpline = new Spline(tempShape);
			tempSpline.translate3D(0, iDistStep*idx, 0);

			pInput = new InputConnector(PARAM_TYPE.SPLINE, `shape_${idx + 1}`, null, tempSpline.saveToObject(), {});
			pNewInputs.push(pInput);
		}

		pNewInputs = pNewInputs.concat([
			this.getInput('smooth_angle'),
			this.getInput('u_offset'),
			this.getInput('v_offset'),
			this.getInput('uv_rotation'),
		]);

		this.updateInputsOutputs(pNewInputs);
	}

	generateSubclass(scene, inputValues, outputName, computedOutputResults)
	{
		let shapeSplines = [];
		let centerPathPoints = [];

		let iNumShapes = this.getLastInputResult('num_shapes');
		let iMaxNumPoints = 0;
		for(let idx = 0; idx < iNumShapes; idx++)
		{
			const inputName = `shape_${idx + 1}`;
			if(!inputValues[inputName])
				continue;

			let spline = new Spline(inputValues[inputName]);
			const iNumPoints = spline.getNumPoints();
			if(iNumPoints === 0)
				continue;

			if(!spline.getIs3D())
				throw new Error(`Curve on input ${inputName} is 2D; only 3D curves expected for this node.`);

			spline.ensureOrientation();
			centerPathPoints.push(spline.getCenter());
			shapeSplines.push(spline);

			if(iNumPoints > iMaxNumPoints)
				iMaxNumPoints = iNumPoints;
		}

		if(shapeSplines.length < 2)
			return null;

		let linearPoints = [];
		
		// Get the first one - resample to max points found:
		const iNumPoints = shapeSplines[0].getNumPoints();
		if(iNumPoints < iMaxNumPoints)
			shapeSplines[0] = shapeSplines[0].resampleByNumber(iMaxNumPoints, 0, 1, 90);
		let currLinearPoints = shapeSplines[0].getLinearPoints3D();
		// For certain rare configurations like zero size rectangles, we could end up 
		// with fewer points due to the getter code above deleting last conincident points.
		// Ensure that's not the case:
		if(currLinearPoints.length > 0 && currLinearPoints.length < iNumPoints)
			currLinearPoints.push(currLinearPoints[0].clone());
		linearPoints.push(currLinearPoints);

		// Now for the rest, find the closest point on each spline:
		for(let idx = 1; idx < shapeSplines.length; idx++)
		{
			let vecNormalDir = centerPathPoints[idx].subtract(centerPathPoints[idx - 1]);

			currLinearPoints = [];
			for(let iPoint = 0; iPoint < linearPoints[0].length; iPoint++)
			{
				let approxPoint = linearPoints[idx - 1][iPoint].add(vecNormalDir);
				let closestPoint = shapeSplines[idx].getPointClosestTo(approxPoint.x, approxPoint.y, approxPoint.z).point;
				currLinearPoints.push(closestPoint);
			}
			linearPoints.push(currLinearPoints);
		}

		// Convert to paths
		let allPaths = [];
		for(let pointIdx = 0; pointIdx < iMaxNumPoints; pointIdx++)
		{
			let currPath = [];
			for(let stepIdx = 0; stepIdx < shapeSplines.length; stepIdx++)
				currPath.push(linearPoints[stepIdx][pointIdx]);
			allPaths.push(currPath);
		}
		const { customUVs, shapeTextureCoords } = Extrude.computeCustomUVsForRibbon(linearPoints[0], allPaths, false);

		let pMesh = BABYLON.MeshBuilder.CreateRibbon(this.getGeneratedMeshName(), { pathArray: allPaths, sideOrientation: BABYLON.Mesh.FRONTSIDE, closeArray: true, closePath: false, uvs: customUVs }, scene);

		let options = {
			smoothAngle: inputValues['smooth_angle'],
			uOffset: inputValues['u_offset'],
			vOffset: inputValues['v_offset'],
			uvRotation: inputValues['uv_rotation'],
			flatShaded: true,
		};
		pMesh = Extrude.capAndFinalizeMesh(scene, pMesh, allPaths, options, BABYLON.Mesh.CAP_ALL, centerPathPoints, false, null, shapeTextureCoords);
		this.onMeshCreated(pMesh);

		return [pMesh];
	}
}